import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Check, Clock, Tag, Plus, ShoppingCart, CheckCircle } from 'lucide-react';
import type { MockProduct } from '@/types/purchaseFlow';
import type { NewItem } from './RichBlockRenderer';

interface ProductListBlockProps {
  data: unknown;
  onAction: (message: string) => void;
  onAddItem?: (item: NewItem) => void;
  created?: boolean;
  isQuotation?: boolean;
}

/** Inline qty popover for a single product */
const QtyPopover: React.FC<{
  product: MockProduct;
  onConfirm: (qty: number) => void;
  onClose: () => void;
}> = ({ product, onConfirm, onClose }) => {
  const [customQty, setCustomQty] = useState('');
  const moq = product.moq || 100;
  const presets = [moq, moq * 2, moq * 5].filter((v, i, a) => a.indexOf(v) === i);

  const handleCustomSubmit = () => {
    const qty = parseInt(customQty, 10);
    if (qty > 0) onConfirm(qty);
  };

  return (
    <div className="mt-2 ml-5.5 flex items-center gap-1.5 flex-wrap animate-fade-in">
      <span className="text-[10px] text-gray-400 mr-0.5">Qty:</span>
      {presets.map(qty => (
        <button
          key={qty}
          onClick={() => onConfirm(qty)}
          className="px-2.5 py-1 rounded-md text-[11px] font-medium border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
        >
          {qty.toLocaleString()}
          {qty === moq && <span className="text-[9px] text-blue-400 ml-0.5">(MOQ)</span>}
        </button>
      ))}
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={customQty}
          onChange={e => setCustomQty(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleCustomSubmit(); }}
          placeholder="Other"
          min={1}
          className="w-20 px-2 py-1 rounded-md text-[11px] border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-1 focus:ring-blue-500 focus:border-transparent"
        />
        {customQty && (
          <button
            onClick={handleCustomSubmit}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            OK
          </button>
        )}
      </div>
      <button
        onClick={onClose}
        className="px-2 py-1 rounded-md text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        ✕
      </button>
    </div>
  );
};

const ProductListBlock: React.FC<ProductListBlockProps> = ({ data, onAction, onAddItem, created, isQuotation }) => {
  const parsed = data as { products?: MockProduct[]; quoted_parts?: Array<Record<string, unknown>>; total?: number };
  const products: MockProduct[] = parsed?.products || [];
  const quotedParts = parsed?.quoted_parts || [];
  const [expandedPart, setExpandedPart] = useState<string | null>(null);
  const [addedParts, setAddedParts] = useState<Set<string>>(new Set());

  // Quotation list rendering
  if (isQuotation && quotedParts.length > 0) {
    return (
      <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              <th className="text-left px-3 py-1.5 font-medium">Part</th>
              <th className="text-right px-3 py-1.5 font-medium">Price</th>
              <th className="text-right px-3 py-1.5 font-medium">Lead</th>
            </tr>
          </thead>
          <tbody>
            {quotedParts.map((part, i) => (
              <tr
                key={i}
                onClick={() => onAction(`Use price from quotation for ${part.part_code}`)}
                className="border-t border-gray-100 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors"
              >
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-700 dark:text-gray-300">{String(part.description)}</div>
                  <div className="text-gray-400">{String(part.part_code)}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono text-gray-700 dark:text-gray-300">
                  {Number(part.best_price).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right text-gray-500">
                  {String(part.lead_time_days)}d
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const { t } = useTranslation('purchaseflow');

  const handleAddWithQty = (product: MockProduct, qty: number) => {
    setExpandedPart(null);

    if (onAddItem) {
      // Direct update — no chat message
      onAddItem({
        part_code: product.part_code,
        product_name: product.description,
        specification: product.specification,
        quantity: qty,
        unit_price: product.last_purchase_price,
        currency: product.currency,
      });
      setAddedParts(prev => new Set(prev).add(product.part_code));
    } else {
      // Fallback to chat message
      onAction(`Add ${product.part_code} ${product.description}, qty: ${qty}`);
    }
  };

  return (
    <div className="space-y-2 mt-2">
      {products.length === 0 && !created && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4 text-xs text-center text-gray-500 dark:text-gray-400">
          <Package className="w-5 h-5 mx-auto mb-1.5 opacity-40" />
          No parts found for this vendor in the AVL.
          <br />Browse the part catalog to add items.
        </div>
      )}
      {products.map(product => {
        const isAdded = addedParts.has(product.part_code);
        return (
          <div
            key={product.part_code}
            className={`rounded-lg border p-3 text-xs transition-colors ${
              created || isAdded
                ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="font-semibold text-gray-800 dark:text-gray-200">{product.description}</span>
                <span className="text-gray-400 font-mono">{product.part_code}</span>
                {(created || isAdded) && <Check className="w-3.5 h-3.5 text-emerald-500" />}
              </div>
            </div>
            <div className="mt-1 ml-5.5 text-gray-500 dark:text-gray-400">
              <div>{product.specification}</div>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{product.currency} {product.last_purchase_price.toFixed(2)}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{product.lead_time_days}d lead</span>
                <span>MOQ: {product.moq.toLocaleString()}</span>
              </div>
            </div>

            {/* Added confirmation */}
            {isAdded && (
              <div className="mt-2 ml-5.5 flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                <CheckCircle className="w-3.5 h-3.5" />
                <span className="text-[11px] font-medium">{t('richBlocks.itemAdded')}</span>
              </div>
            )}

            {/* Add button — hidden if already added */}
            {!created && !isAdded && expandedPart !== product.part_code && (
              <button
                onClick={() => setExpandedPart(product.part_code)}
                className="mt-2 ml-5.5 px-3 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 transition-colors flex items-center gap-1.5"
              >
                <ShoppingCart className="w-3 h-3" />
                {t('richBlocks.addToPO')}
              </button>
            )}

            {!created && !isAdded && expandedPart === product.part_code && (
              <QtyPopover
                product={product}
                onConfirm={(qty) => handleAddWithQty(product, qty)}
                onClose={() => setExpandedPart(null)}
              />
            )}
          </div>
        );
      })}
      {!created && (
        <button
          onClick={() => onAction('Browse parts')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> {t('quickActions.addVendorPart')}
        </button>
      )}
    </div>
  );
};

export default ProductListBlock;
